<template>
  <AdminLayout>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header -->
      <div class="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <div class="p-2 bg-[#22282F]/10 rounded-lg">
              <BarChart3 class="w-7 h-7 sm:w-8 sm:h-8 text-[#22282F]" />
            </div>
            Statistiques de connexion
          </h1>
          <p class="text-gray-500 mt-2">Analyse des connexions à partir des journaux d'audit</p>
        </div>
        <button
          @click="refresh"
          :disabled="loading"
          class="self-start sm:self-auto px-4 py-2 bg-[#22282F] text-white rounded-lg hover:bg-[#2a3139] transition-colors flex items-center gap-2 disabled:opacity-50"
        >
          <RefreshCw :class="['w-4 h-4', loading && 'animate-spin']" />
          Actualiser
        </button>
      </div>

      <!-- Filtres -->
      <div class="bg-white border border-gray-200 rounded-xl shadow-sm p-4 mb-6">
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          <div>
            <label class="block text-xs font-medium text-gray-500 mb-1.5">Période</label>
            <select v-model.number="period" class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/15">
              <option :value="1">Aujourd'hui</option>
              <option :value="7">7 derniers jours</option>
              <option :value="30">30 derniers jours</option>
              <option :value="90">90 derniers jours</option>
              <option :value="0">Personnalisé…</option>
            </select>
          </div>
          <template v-if="period === 0">
            <div>
              <label class="block text-xs font-medium text-gray-500 mb-1.5">Du</label>
              <input v-model="customFrom" type="date" class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/15" />
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-500 mb-1.5">Au</label>
              <input v-model="customTo" type="date" class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/15" />
            </div>
          </template>
          <div>
            <label class="block text-xs font-medium text-gray-500 mb-1.5">Ambassade</label>
            <select v-model="ambassadeId" class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/15">
              <option :value="null">Toutes</option>
              <option v-for="a in ambassades" :key="a.id" :value="a.id">{{ a.nom }}</option>
            </select>
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-500 mb-1.5">Direction</label>
            <select v-model="directionId" class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/15">
              <option :value="null">Toutes</option>
              <option v-for="d in directions" :key="d.id" :value="d.id">{{ d.nom }}</option>
            </select>
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-500 mb-1.5">Rôle</label>
            <select v-model="roleCode" class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/15">
              <option :value="null">Tous</option>
              <option v-for="r in roles" :key="r.code" :value="r.code">{{ r.nom }}</option>
            </select>
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-500 mb-1.5">Agent</label>
            <select v-model="userId" class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/15">
              <option :value="null">Tous</option>
              <option v-for="u in usersList" :key="u.id" :value="u.id">{{ u.name }}</option>
            </select>
          </div>
        </div>
        <div v-if="hasActiveFilters" class="mt-3 flex justify-end">
          <button
            @click="resetFilters"
            class="px-3 py-2 text-sm text-gray-500 hover:text-[#22282F] border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1.5"
          >
            <X class="w-4 h-4" /> Réinitialiser les filtres
          </button>
        </div>
      </div>

      <!-- Erreur -->
      <div v-if="error" class="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
        <AlertCircle class="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
        <p class="text-sm text-red-800">{{ error }}</p>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex items-center justify-center py-20">
        <div class="text-center">
          <div class="w-12 h-12 border-4 border-[#22282F]/30 border-t-[#22282F] rounded-full animate-spin mx-auto mb-4" />
          <p class="text-gray-500">Chargement des statistiques…</p>
        </div>
      </div>

      <template v-else>
        <!-- KPIs -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6">
          <div class="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <div class="flex items-center justify-between mb-2">
              <div class="p-2.5 bg-[#22282F]/10 rounded-xl"><Users class="w-5 h-5 text-[#22282F]" /></div>
              <span class="text-xs text-gray-400">Aujourd'hui</span>
            </div>
            <p class="text-3xl font-bold text-gray-900">{{ kpi.todayUsers }}</p>
            <p class="text-sm text-gray-500 mt-0.5">personnes connectées aujourd'hui</p>
            <p class="text-xs text-gray-400 mt-1">{{ kpi.todayTotal }} connexion(s) au total</p>
          </div>

          <div class="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <div class="flex items-center justify-between mb-2">
              <div class="p-2.5 bg-blue-50 rounded-xl"><LogIn class="w-5 h-5 text-blue-600" /></div>
              <span class="text-xs text-gray-400">{{ periodLabel }}</span>
            </div>
            <p class="text-3xl font-bold text-gray-900">{{ kpi.totalLogins }}</p>
            <p class="text-sm text-gray-500 mt-0.5">connexions sur la période</p>
          </div>

          <div class="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <div class="flex items-center justify-between mb-2">
              <div class="p-2.5 bg-green-50 rounded-xl"><UserCheck class="w-5 h-5 text-green-600" /></div>
              <span class="text-xs text-gray-400">{{ periodLabel }}</span>
            </div>
            <p class="text-3xl font-bold text-gray-900">{{ kpi.activeUsers }}</p>
            <p class="text-sm text-gray-500 mt-0.5">utilisateurs actifs distincts</p>
          </div>

          <div class="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <div class="flex items-center justify-between mb-2">
              <div class="p-2.5 bg-amber-50 rounded-xl"><Clock class="w-5 h-5 text-amber-600" /></div>
              <span class="text-xs text-gray-400">moyenne</span>
            </div>
            <p class="text-3xl font-bold text-gray-900">{{ kpi.avgSession }}</p>
            <p class="text-sm text-gray-500 mt-0.5">durée moyenne de session</p>
            <p class="text-xs text-gray-400 mt-1">{{ kpi.totalUsage }} cumulées</p>
          </div>
        </div>

        <!-- Activité de la plateforme -->
        <div class="mb-3 mt-2 flex items-center gap-2">
          <Workflow class="w-5 h-5 text-[#22282F]" />
          <h2 class="text-lg font-semibold text-gray-900">Activité de la plateforme</h2>
          <span class="text-xs text-gray-400">· {{ periodLabel }}</span>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6 mb-6">
          <div class="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <div class="flex items-center justify-between mb-2">
              <div class="p-2.5 bg-indigo-50 rounded-xl"><Percent class="w-5 h-5 text-indigo-600" /></div>
              <span class="text-xs text-gray-400">{{ periodLabel }}</span>
            </div>
            <p class="text-3xl font-bold text-gray-900">{{ activity.tauxConnexion }}%</p>
            <p class="text-sm text-gray-500 mt-0.5">taux de connexion</p>
            <p class="text-xs text-gray-400 mt-1">{{ activity.actifs }} / {{ activity.total }} compte(s)</p>
          </div>

          <div class="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <div class="flex items-center justify-between mb-2">
              <div class="p-2.5 bg-[#22282F]/10 rounded-xl"><FileText class="w-5 h-5 text-[#22282F]" /></div>
              <span class="text-xs text-gray-400">{{ periodLabel }}</span>
            </div>
            <p class="text-3xl font-bold text-gray-900">{{ activity.notesCreated }}</p>
            <p class="text-sm text-gray-500 mt-0.5">notes créées</p>
          </div>

          <div class="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <div class="flex items-center justify-between mb-2">
              <div class="p-2.5 bg-gray-100 rounded-xl"><Send class="w-5 h-5 text-gray-600" /></div>
              <span class="text-xs text-gray-400">{{ periodLabel }}</span>
            </div>
            <p class="text-3xl font-bold text-gray-900">{{ activity.notesSubmitted }}</p>
            <p class="text-sm text-gray-500 mt-0.5">notes soumises au workflow</p>
          </div>

          <div class="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <div class="flex items-center justify-between mb-2">
              <div class="p-2.5 bg-blue-50 rounded-xl"><ClipboardList class="w-5 h-5 text-blue-600" /></div>
              <span class="text-xs text-gray-400">{{ periodLabel }}</span>
            </div>
            <p class="text-3xl font-bold text-gray-900">{{ activity.affCreated }}</p>
            <p class="text-sm text-gray-500 mt-0.5">affectations créées</p>
          </div>

          <div class="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <div class="flex items-center justify-between mb-2">
              <div class="p-2.5 bg-green-50 rounded-xl"><CheckCircle2 class="w-5 h-5 text-green-600" /></div>
              <span class="text-xs text-gray-400">{{ periodLabel }}</span>
            </div>
            <p class="text-3xl font-bold text-gray-900">{{ activity.workflowsValides }}</p>
            <p class="text-sm text-gray-500 mt-0.5">workflows validés</p>
            <p class="text-xs text-gray-400 mt-1">{{ activity.notesValidees }} note(s) · {{ activity.affTraitees }} affectation(s)</p>
          </div>
        </div>

        <!-- Répartition de l'activité -->
        <div class="bg-white border border-gray-200 rounded-xl p-5 shadow-sm mb-6">
          <h3 class="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <BarChart3 class="w-4 h-4 text-[#22282F]" /> Répartition de l'activité
          </h3>
          <p class="text-xs text-gray-400 mb-4">Volume d'événements métier audités sur la période</p>
          <div class="h-64">
            <Bar v-if="hasActivity" :data="activityData" :options="activityOptions" />
            <EmptyChart v-else />
          </div>
        </div>

        <!-- Charts row 1 -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div class="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <h3 class="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp class="w-4 h-4 text-[#22282F]" /> Connexions par jour
            </h3>
            <div class="h-64">
              <Line v-if="hasData" :data="lineData" :options="lineOptions" />
              <EmptyChart v-else />
            </div>
          </div>
          <div class="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <h3 class="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <PieChart class="w-4 h-4 text-[#22282F]" /> Répartition par ambassade
            </h3>
            <div class="h-64 flex items-center justify-center">
              <Doughnut v-if="hasData" :data="doughnutData" :options="doughnutOptions" />
              <EmptyChart v-else />
            </div>
          </div>
        </div>

        <!-- Charts row 2 -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div class="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <h3 class="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
              <Activity class="w-4 h-4 text-[#22282F]" /> Pics horaires de connexion
            </h3>
            <p class="text-xs text-gray-400 mb-4">Nombre de connexions par heure de la journée</p>
            <div class="h-64">
              <Bar v-if="hasData" :data="hourData" :options="hourOptions" />
              <EmptyChart v-else />
            </div>
          </div>
          <div class="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <h3 class="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
              <CalendarDays class="w-4 h-4 text-[#22282F]" /> Connexions par jour de semaine
            </h3>
            <p class="text-xs text-gray-400 mb-4">Identifie les journées les plus actives</p>
            <div class="h-64">
              <Bar v-if="hasData" :data="weekdayData" :options="weekdayOptions" />
              <EmptyChart v-else />
            </div>
          </div>
        </div>

        <!-- Top users -->
        <div class="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <h3 class="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Trophy class="w-4 h-4 text-[#22282F]" /> Utilisateurs les plus actifs
          </h3>
          <div v-if="top.length" class="space-y-3">
            <div v-for="(u, i) in top" :key="u.id" class="flex items-center gap-3">
              <span class="flex-shrink-0 w-6 h-6 rounded-full text-xs font-semibold flex items-center justify-center"
                :class="i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'">
                {{ i + 1 }}
              </span>
              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between gap-2 mb-1">
                  <p class="text-sm font-medium text-gray-900 truncate">{{ u.name }}</p>
                  <span class="text-xs text-gray-500 flex-shrink-0">{{ u.count }} connexion(s)</span>
                </div>
                <div class="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div class="h-full bg-[#22282F] rounded-full" :style="{ width: barWidth(u.count) }" />
                </div>
                <p v-if="u.ambassade" class="text-xs text-gray-400 mt-1">{{ u.ambassade }}</p>
              </div>
            </div>
          </div>
          <p v-else class="text-sm text-gray-400 py-6 text-center">Aucune connexion sur la période.</p>
        </div>
      </template>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { defineComponent, h } from 'vue'
import {
  BarChart3, RefreshCw, AlertCircle, Users, LogIn, UserCheck, Clock,
  TrendingUp, PieChart, Activity, CalendarDays, Trophy,
  Workflow, Percent, FileText, Send, ClipboardList, CheckCircle2, X,
} from 'lucide-vue-next'
import { Bar, Line, Doughnut } from 'vue-chartjs'
import {
  Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale,
  LinearScale, PointElement, LineElement, ArcElement, Filler,
} from 'chart.js'
import AdminLayout from '@/layouts/AdminLayout.vue'
import { useToast } from '@/utils/toast'
import {
  fetchAuditLogs, fetchUserMap, filterLogs, dayRange, loginsPerDay,
  loginsPerHour, loginsPerWeekday, distinctUsers, todayStats, buildSessions,
  avgSessionMs, totalSessionMs, topUsers, loginsPerAmbassade, formatDuration,
  listAmbassades, listRoles, listDirections, listUsers,
} from '@/utils/loginStats'
import type { AuditLog, UserMap, UserInfo } from '@/utils/loginStats'

ChartJS.register(
  Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale,
  PointElement, LineElement, ArcElement, Filler,
)

// Petit composant d'état vide pour les graphiques
const EmptyChart = defineComponent({
  setup() {
    return () => h('div', { class: 'h-full w-full flex items-center justify-center text-sm text-gray-400' }, 'Aucune donnée sur la période')
  },
})

const toast = useToast()

const loading = ref(true)
const error = ref('')
// period : 1/7/30/90 = presets glissants ; 0 = intervalle personnalisé (customFrom/customTo)
const period = ref<number>(7)
const customFrom = ref<string>('')
const customTo = ref<string>('')
const ambassadeId = ref<number | null>(null)
const directionId = ref<number | null>(null)
const roleCode = ref<string | null>(null)
const userId = ref<number | null>(null)

const userMap = ref<UserMap>({})
const rawLogins = ref<AuditLog[]>([])
const rawLogouts = ref<AuditLog[]>([])
// Activité plateforme (mêmes filtres ambassade/rôle appliqués côté client)
const rawNotesCreated = ref<AuditLog[]>([])
const rawNotesSubmitted = ref<AuditLog[]>([])
const rawValidations = ref<AuditLog[]>([])
const rawAffCreated = ref<AuditLog[]>([])
const rawAffTreated = ref<AuditLog[]>([])

const ambassades = computed(() => listAmbassades(userMap.value))
const directions = computed(() => listDirections(userMap.value))
const roles = computed(() => listRoles(userMap.value))
const usersList = computed(() => listUsers(userMap.value))

const hasActiveFilters = computed(() =>
  Boolean(ambassadeId.value || directionId.value || roleCode.value || userId.value),
)

const periodLabel = computed(() => {
  if (period.value === 0) {
    if (customFrom.value && customTo.value) return `${customFrom.value} → ${customTo.value}`
    return 'intervalle personnalisé'
  }
  if (period.value === 1) return "aujourd'hui"
  return `${period.value} j`
})

// Critères de filtrage client (ambassade / direction / rôle / agent)
const filterOpts = computed(() => ({
  ambassadeId: ambassadeId.value,
  directionId: directionId.value,
  roleCode: roleCode.value,
  userId: userId.value,
}))

// Logs filtrés selon le périmètre
const logins = computed(() =>
  filterLogs(rawLogins.value, userMap.value, filterOpts.value),
)
const logouts = computed(() =>
  filterLogs(rawLogouts.value, userMap.value, filterOpts.value),
)

// Activité plateforme filtrée selon le périmètre
const applyFilter = (logs: AuditLog[]) =>
  filterLogs(logs, userMap.value, filterOpts.value)
const notesCreated = computed(() => applyFilter(rawNotesCreated.value))
const notesSubmitted = computed(() => applyFilter(rawNotesSubmitted.value))
const validations = computed(() => applyFilter(rawValidations.value))
const affCreated = computed(() => applyFilter(rawAffCreated.value))
const affTreated = computed(() => applyFilter(rawAffTreated.value))

const hasData = computed(() => logins.value.length > 0)

const sessions = computed(() => buildSessions(logins.value, logouts.value))

const kpi = computed(() => {
  const today = todayStats(logins.value)
  return {
    todayUsers: today.users,
    todayTotal: today.total,
    totalLogins: logins.value.length,
    activeUsers: distinctUsers(logins.value),
    avgSession: formatDuration(avgSessionMs(sessions.value)),
    totalUsage: formatDuration(totalSessionMs(sessions.value)),
  }
})

// Nombre de comptes correspondant au périmètre, pour le taux de connexion
const totalAccounts = computed(() => {
  const all: UserInfo[] = Object.values(userMap.value)
  if (!hasActiveFilters.value) return all.length
  return all.filter((u) =>
    (!ambassadeId.value || u.ambassade?.id === ambassadeId.value) &&
    (!directionId.value || u.direction?.id === directionId.value) &&
    (!roleCode.value || u.role?.code === roleCode.value) &&
    (!userId.value || u.id === userId.value),
  ).length
})

// KPIs d'activité plateforme (sur la période, dérivés des actions auditées)
const activity = computed(() => {
  const notesValidees = validations.value.length
  const affTraitees = affTreated.value.length
  const total = totalAccounts.value
  const actifs = distinctUsers(logins.value)
  return {
    tauxConnexion: total > 0 ? Math.round((actifs / total) * 100) : 0,
    actifs,
    total,
    notesCreated: notesCreated.value.length,
    notesSubmitted: notesSubmitted.value.length,
    affCreated: affCreated.value.length,
    notesValidees,
    affTraitees,
    workflowsValides: notesValidees + affTraitees,
  }
})

const top = computed(() => topUsers(logins.value, userMap.value, 5))
const maxTop = computed(() => (top.value.length ? top.value[0].count : 1))
const barWidth = (count: number) => `${Math.round((count / maxTop.value) * 100)}%`

// ---- Période -> dates ----
function periodRange(): { from: Date; to: Date } {
  // Intervalle personnalisé
  if (period.value === 0 && customFrom.value && customTo.value) {
    const from = new Date(customFrom.value)
    from.setHours(0, 0, 0, 0)
    const to = new Date(customTo.value)
    to.setHours(23, 59, 59, 999)
    return { from, to }
  }
  // Presets glissants (1/7/30/90) — fallback 7 j si custom incomplet
  const days = period.value > 0 ? period.value : 7
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - (days - 1))
  from.setHours(0, 0, 0, 0)
  return { from, to }
}

function resetFilters() {
  ambassadeId.value = null
  directionId.value = null
  roleCode.value = null
  userId.value = null
}

// ---- Données des graphiques ----
const PALETTE = ['#22282F', '#8C0004', '#4B5563', '#9CA3AF', '#1D4ED8', '#059669', '#D97706', '#7C3AED', '#DB2777', '#0891B2']

const lineData = computed(() => {
  const { from, to } = periodRange()
  const keys = dayRange(from, to)
  const perDay = loginsPerDay(logins.value)
  return {
    labels: keys.map((k) => {
      const d = new Date(k)
      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
    }),
    datasets: [{
      label: 'Connexions',
      data: keys.map((k) => perDay[k] || 0),
      borderColor: '#22282F',
      backgroundColor: 'rgba(34,40,47,0.08)',
      fill: true,
      tension: 0.35,
      pointRadius: 3,
      pointBackgroundColor: '#22282F',
    }],
  }
})

const hourData = computed(() => ({
  labels: Array.from({ length: 24 }, (_, h2) => `${String(h2).padStart(2, '0')}h`),
  datasets: [{
    label: 'Connexions',
    data: loginsPerHour(logins.value),
    backgroundColor: '#22282F',
    borderRadius: 4,
    maxBarThickness: 18,
  }],
}))

const weekdayData = computed(() => ({
  labels: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'],
  datasets: [{
    label: 'Connexions',
    data: loginsPerWeekday(logins.value),
    backgroundColor: '#8C0004',
    borderRadius: 4,
    maxBarThickness: 32,
  }],
}))

const doughnutData = computed(() => {
  const grouped = loginsPerAmbassade(logins.value, userMap.value)
  const entries = Object.entries(grouped).sort((a, b) => b[1] - a[1]).slice(0, 10)
  return {
    labels: entries.map((e) => e[0]),
    datasets: [{
      data: entries.map((e) => e[1]),
      backgroundColor: entries.map((_, i) => PALETTE[i % PALETTE.length]),
      borderWidth: 2,
      borderColor: '#fff',
    }],
  }
})

const activityData = computed(() => ({
  labels: ['Notes créées', 'Notes soumises', 'Affectations créées', 'Notes validées', 'Affectations traitées'],
  datasets: [{
    label: 'Événements',
    data: [
      activity.value.notesCreated,
      activity.value.notesSubmitted,
      activity.value.affCreated,
      activity.value.notesValidees,
      activity.value.affTraitees,
    ],
    backgroundColor: ['#22282F', '#4B5563', '#1D4ED8', '#059669', '#D97706'],
    borderRadius: 4,
    maxBarThickness: 48,
  }],
}))
const hasActivity = computed(() =>
  activity.value.notesCreated + activity.value.notesSubmitted + activity.value.affCreated +
  activity.value.notesValidees + activity.value.affTraitees > 0,
)

const baseOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
}
const lineOptions = { ...baseOptions, scales: { y: { beginAtZero: true, ticks: { precision: 0 } } } }
const hourOptions = { ...baseOptions, scales: { y: { beginAtZero: true, ticks: { precision: 0 } } } }
const weekdayOptions = { ...baseOptions, scales: { y: { beginAtZero: true, ticks: { precision: 0 } } } }
const activityOptions = { ...baseOptions, scales: { y: { beginAtZero: true, ticks: { precision: 0 } } } }
const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '62%',
  plugins: { legend: { position: 'bottom' as const, labels: { boxWidth: 12, font: { size: 11 } } } },
}

// ---- Chargement ----
async function load() {
  loading.value = true
  error.value = ''
  try {
    const { from, to } = periodRange()
    const fromISO = from.toISOString()
    const toISO = to.toISOString()
    const [map, lins, louts, nCreated, nSubmitted, vals, aCreated, aTreated] = await Promise.all([
      Object.keys(userMap.value).length ? Promise.resolve(userMap.value) : fetchUserMap(),
      fetchAuditLogs('auth.login_success', fromISO, toISO),
      fetchAuditLogs('auth.logout', fromISO, toISO),
      fetchAuditLogs('note.creee', fromISO, toISO),
      fetchAuditLogs('note.soumise', fromISO, toISO),
      fetchAuditLogs('validation.validee', fromISO, toISO),
      fetchAuditLogs('affectation.creee', fromISO, toISO),
      fetchAuditLogs('affectation.traitee', fromISO, toISO),
    ])
    userMap.value = map
    rawLogins.value = lins
    rawLogouts.value = louts
    rawNotesCreated.value = nCreated
    rawNotesSubmitted.value = nSubmitted
    rawValidations.value = vals
    rawAffCreated.value = aCreated
    rawAffTreated.value = aTreated
  } catch (e: any) {
    error.value = "Impossible de charger les statistiques de connexion"
    toast.error('Erreur', e?.message || 'Chargement des statistiques échoué')
  } finally {
    loading.value = false
  }
}

function refresh() {
  userMap.value = {}
  load()
}

// Recharger les logs quand la fenêtre temporelle change. Les filtres
// périmètre (ambassade/direction/rôle/agent) sont appliqués côté client
// sans refetch.
let rangeDebounce: ReturnType<typeof setTimeout> | undefined
watch(period, (p) => {
  // À l'entrée en mode personnalisé, pré-remplir un intervalle par défaut (30 j).
  if (p === 0 && (!customFrom.value || !customTo.value)) {
    const to = new Date()
    const from = new Date(to.getTime() - 30 * 24 * 3600 * 1000)
    customFrom.value = from.toISOString().slice(0, 10)
    customTo.value = to.toISOString().slice(0, 10)
  }
  load()
})
watch([customFrom, customTo], () => {
  if (period.value !== 0) return
  if (!customFrom.value || !customTo.value) return
  if (rangeDebounce) clearTimeout(rangeDebounce)
  rangeDebounce = setTimeout(() => load(), 300)
})

onMounted(load)
</script>
